/**
 * Runtime detection: browser vs Tauri shell.
 * Safe for SSR: only checks window when available.
 */

import type { PlatformType, RuntimeType } from './types'

declare global {
  interface Window {
    __TAURI__?: unknown
  }
}

export function getRuntime(): RuntimeType {
  if (typeof window === 'undefined') return 'browser'
  return '__TAURI__' in window ? 'tauri' : 'browser'
}

export function isInTauriShell(): boolean {
  return getRuntime() === 'tauri'
}

/**
 * Returns platform when in Tauri; otherwise derives from navigator (best-effort).
 */
export function getPlatformFallback(): PlatformType {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('win')) return 'windows'
  if (ua.includes('mac')) return 'macos'
  if (ua.includes('android')) return 'android'
  if (ua.includes('iphone') || ua.includes('ipad')) return 'ios'
  if (ua.includes('linux')) return 'linux'
  return 'unknown'
}

/**
 * Returns true when running on a mobile/touch platform (Android or iOS).
 * Use this to gate UI layout decisions — not capability checks (use getCapabilities() for those).
 */
export function isMobilePlatform(): boolean {
  const platform = getPlatformFallback()
  return platform === 'android' || platform === 'ios'
}

/**
 * Returns true when running on a tablet-class device.
 * Heuristic: mobile platform + screen short-side >= 600 logical pixels.
 */
export function isTabletPlatform(): boolean {
  if (!isMobilePlatform()) return false
  if (typeof window === 'undefined') return false
  const shortSide = Math.min(window.screen.width, window.screen.height)
  return shortSide >= 600
}
